import { useMemo, useRef, useState, type CSSProperties } from 'react';
import { motion } from 'framer-motion';

/** One box's grapheme + (optional) sound info — the only shape BuildWord needs. */
export interface BuildBox {
  graphemes: string;
  phoneme: string;
  audioId: string;
}
export interface BuildableWord {
  boxes: BuildBox[];
}

interface Props {
  word: BuildableWord;
  /** All boxes filled correctly. firstTry = no wrong placements along the way. */
  onComplete: (firstTry: boolean) => void;
  /** A sound was placed (or replayed) — play that phoneme. */
  onPlaceCorrect: (audioId: string) => void;
  /** Wrong tile for this box — Spidey models the correct sound. */
  onWrong: (boxIndex: number, expectedAudioId: string) => void;
  /** Tapped an empty box with nothing selected — coach the needed sound. */
  onHintBox: (audioId: string) => void;
}

interface Tile {
  id: string;
  graphemes: string;
  audioId: string;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Shuffle the tiles so they NEVER start in the correct order (else the rebuild is
 *  trivially solved). Compares the grapheme sequence so repeated graphemes are handled;
 *  bails after a few tries (e.g. a 1-tile word can't differ). */
function scramble(tiles: Tile[]): Tile[] {
  const target = tiles.map((t) => t.graphemes).join('');
  for (let i = 0; i < 12; i++) {
    const s = shuffle(tiles);
    if (s.map((t) => t.graphemes).join('') !== target) return s;
  }
  return shuffle(tiles);
}

/**
 * The no-guess production gate. Boxes start empty; the word's own sound-tiles (no
 * distractor foils) sit shuffled below. The child places each by sound. Every wrong
 * placement is met warmly — Spidey models that box's sound — then he retries. There
 * is no way to advance without producing the word.
 */
export function BuildWord({ word, onComplete, onPlaceCorrect, onWrong, onHintBox }: Props) {
  const tiles = useMemo<Tile[]>(
    () => scramble(word.boxes.map((b, i) => ({ id: `t${i}`, graphemes: b.graphemes, audioId: b.audioId }))),
    [], // scramble once on mount; StoryScreen remounts this per beat via `key`
  );
  const [placed, setPlaced] = useState<(string | null)[]>(() => word.boxes.map(() => null));
  const [selected, setSelected] = useState<string | null>(null);
  const [wrongBox, setWrongBox] = useState<number | null>(null);
  const hadWrong = useRef(false);

  const tapTile = (tile: Tile) => {
    if (placed.includes(tile.id)) return;
    setSelected((cur) => (cur === tile.id ? null : tile.id));
  };

  const tapBox = (i: number) => {
    if (placed[i]) {
      onPlaceCorrect(word.boxes[i].audioId);
      return;
    }
    if (!selected) {
      onHintBox(word.boxes[i].audioId);
      return;
    }
    const tile = tiles.find((t) => t.id === selected);
    if (!tile) return;
    if (tile.graphemes === word.boxes[i].graphemes) {
      const next = [...placed];
      next[i] = tile.id;
      setPlaced(next);
      setSelected(null);
      onPlaceCorrect(word.boxes[i].audioId);
      if (next.every(Boolean)) onComplete(!hadWrong.current);
    } else {
      hadWrong.current = true;
      setSelected(null);
      setWrongBox(i);
      onWrong(i, word.boxes[i].audioId);
      window.setTimeout(() => setWrongBox(null), 520);
    }
  };

  return (
    <div style={wrap}>
      <div style={boxRow}>
        {word.boxes.map((b, i) => {
          const filled = placed[i] != null;
          return (
            <motion.button
              key={i}
              type="button"
              onClick={() => tapBox(i)}
              animate={wrongBox === i ? { x: [0, -9, 9, -7, 7, 0] } : { x: 0 }}
              transition={{ duration: 0.5 }}
              style={{ ...box, ...(filled ? boxFilled : null) }}
              aria-label={filled ? `${b.phoneme} placed` : `empty box, needs ${b.phoneme}`}
            >
              {filled ? <span style={grapheme}>{b.graphemes}</span> : <span style={slot}>?</span>}
            </motion.button>
          );
        })}
      </div>

      <div style={tray}>
        {tiles.map((t) => {
          const used = placed.includes(t.id);
          const isSel = selected === t.id;
          return (
            <motion.button
              key={t.id}
              type="button"
              onClick={() => tapTile(t)}
              disabled={used}
              whileTap={{ scale: 0.92 }}
              style={{ ...tile, ...(isSel ? tileSel : null), ...(used ? tileUsed : null) }}
              aria-label={`sound tile ${t.graphemes}`}
            >
              <span style={grapheme}>{t.graphemes}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

const wrap: CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 26 };
const boxRow: CSSProperties = { display: 'flex', gap: 16, justifyContent: 'center' };
const tray: CSSProperties = { display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' };

const box: CSSProperties = {
  minWidth: 104,
  height: 128,
  borderRadius: 'var(--radius)',
  borderWidth: 4,
  borderStyle: 'dashed',
  borderColor: 'var(--box-border)',
  background: 'var(--box)',
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
};
const boxFilled: CSSProperties = {
  borderStyle: 'solid',
  borderColor: 'var(--box-done-border)',
  background: 'var(--box-done)',
};

const slot: CSSProperties = { fontSize: 44, color: 'var(--box-border)', fontWeight: 800 };

const tile: CSSProperties = {
  minWidth: 92,
  height: 96,
  padding: '0 10px',
  borderRadius: 16,
  borderWidth: 3,
  borderStyle: 'solid',
  borderColor: 'var(--hero-blue)',
  background: '#eaf1ff',
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
};
const tileSel: CSSProperties = {
  background: 'var(--box-active-bg)',
  borderColor: 'var(--box-active-border)',
  transform: 'translateY(-6px) scale(1.06)',
};
const tileUsed: CSSProperties = { opacity: 0.25, pointerEvents: 'none' };

const grapheme: CSSProperties = {
  fontFamily: 'var(--font-read)',
  fontSize: 'var(--grapheme-size)',
  fontWeight: 800,
  color: 'var(--ink)',
  lineHeight: 1,
};
